import { useState } from 'react';
import { useCurrency } from '@/context/CurrencyContext';
import { BalanceSheetLine, BalanceSheetOverviewData } from '@/types/balance-sheet-overview.api';
import i18n from '@/i18n';
import { Body0, Body1, Body1Muted, H4 } from '@/components/ui/Typography';
import FoldOutButton from '@/components/ui/ButtonFoldOut';
import { render } from 'react-dom';

interface BalanceSheetTableProps { data: BalanceSheetOverviewData; }

const BalanceSheetTable = ({ data }: BalanceSheetTableProps) => {
    const { formatNumber } = useCurrency();
    const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
    const locale = i18n.language

    const toggle = (catKey: string) => {
        setExpandedCategories(prev => ({ ...prev, [catKey]: !prev[catKey] }));
    };

    const indent = ['pl-2', 'pl-6', 'pl-12'];

    const renderLeaf = (leaf: BalanceSheetLine, catKey: string) => {
        const leafKey = `${catKey}-${leaf.key}`;
        return (
            <div
                key={leafKey}
                className={`flex justify-between items-center py-1 ${indent[2]}`}
            >
                <Body1Muted>{leaf.key} - {leaf.title[locale]}</Body1Muted>
                <Body1Muted className="text-right w-1/4">{formatNumber(leaf.total)}</Body1Muted>
            </div>
        );
    };

    const renderCategory = (category: BalanceSheetLine, rowKey: string) => {
        const catKey = `${rowKey}-${category.key}`;
        const hasLeaves = category.subRows?.length > 0;
        const isExpanded = !!expandedCategories[catKey];

        return (
            <div key={catKey}>
                <div
                    className={`flex justify-between items-center cursor-pointer py-1 ${indent[1]}`}
                    onClick={() => hasLeaves && toggle(catKey)}
                >
                    <div className="flex items-center">
                        {hasLeaves ? (
                            <FoldOutButton
                                isExpanded={isExpanded}
                                toggle={toggle}
                                keyValue={catKey}
                            />
                        ) : (
                            <span className="inline-block w-4 mr-2" />
                        )}
                        <Body1>{category.title[locale]}</Body1>
                    </div>
                    <Body1 className="text-right w-1/4">{formatNumber(category.total)}</Body1>
                </div>

                {hasLeaves && isExpanded && (
                    <div>
                        {category.subRows!.map(leaf => renderLeaf(leaf, catKey))}
                    </div>
                )}
            </div>
        );
    };

    const renderRow = (row: BalanceSheetLine) => {
        const rowKey = `${row.key}`;
        return (
            <div key={rowKey} className="flex flex-col">
                <div className={`flex justify-between items-center py-2 ${indent[0]}`}>
                    <Body0>{row.title[locale]}</Body0>
                    <Body0 className="text-right w-1/4">{formatNumber(row.total)}</Body0>
                </div>
                <Body1>
                    {row.subRows?.map(category => renderCategory(category, rowKey))}
                </Body1>
            </div >
        );
    };

    const renderGroup = (
        group: BalanceSheetOverviewData['assets'] | BalanceSheetOverviewData['liabilitiesAndEquity']
    ) => (

        <div className="flex flex-col gap-2 w-full" key={group.title[locale]}>
            <H4>{group.title[locale]}</H4>
            <Body1> {group.rows.map(row => renderRow(row))} </Body1>
            <div className="flex justify-between font-bold pt-2 border-t border-gray-200 mt-2 pl-2">
                <H4>{group.title[locale]}</H4>
                <H4 className="text-right w-1/4">{formatNumber(group.total)}</H4>
            </div>
        </div>
    );

    return (
        <div>
            <div className="grid grid-cols-2 gap-12">
                {renderGroup(data.assets)}
                {renderGroup(data.liabilitiesAndEquity)}
            </div>
            <div className="flex justify-between font-bold pt-2 border-t border-gray-200 mt-10 pl-2">
                <H4>{data.expectedResult.title[locale]}</H4>
                <H4 className="text-right w-1/4">{formatNumber(data.expectedResult.total)}</H4>
            </div>
        </div>
    );
};

export default BalanceSheetTable;
